import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Server, Lock, Send, CheckCircle2, XCircle, Loader2,
  Shield, Save, AlertTriangle, Info
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SMTP_PRESETS = [
  { name: 'Custom', host: '', port: 587, use_tls: true },
  { name: 'Gmail', host: 'smtp.gmail.com', port: 587, use_tls: true },
  { name: 'Outlook/Office 365', host: 'smtp.office365.com', port: 587, use_tls: true },
  { name: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 587, use_tls: true },
  { name: 'Zoho', host: 'smtp.zoho.com', port: 587, use_tls: true },
];

export default function SmtpSettingsPage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [lastTested, setLastTested] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('Custom');

  const [form, setForm] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
  });

  const [testForm, setTestForm] = useState({
    recipient: '',
    message: '',
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/api/smtp-settings`, { headers });
      const data = await res.json();
      if (data && data.smtp_host) {
        setForm({
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || '',
          smtp_password: '',
          from_email: data.from_email || '',
          from_name: data.from_name || '',
          use_tls: data.use_tls !== false,
        });
        setIsVerified(data.is_verified || false);
        setLastTested(data.last_tested || null);
        setHasExisting(true);
        // Detect preset
        const preset = SMTP_PRESETS.find(p => p.host === data.smtp_host);
        if (preset) setSelectedPreset(preset.name);
      }
    } catch (e) {
      console.error('Failed to load SMTP settings', e);
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (presetName) => {
    setSelectedPreset(presetName);
    const preset = SMTP_PRESETS.find(p => p.name === presetName);
    if (preset) {
      setForm(prev => ({
        ...prev,
        smtp_host: preset.host,
        smtp_port: preset.port,
        use_tls: preset.use_tls,
      }));
    }
  };

  const handlePortChange = (port) => {
    const portNum = parseInt(port) || 587;
    setForm(prev => ({
      ...prev,
      smtp_port: portNum,
      use_tls: portNum !== 465 ? prev.use_tls : false,
    }));
  };

  const handleSave = async () => {
    if (!form.smtp_host || !form.smtp_username || !form.from_email || !form.from_name) {
      toast.error(t('smtp_fill_required'));
      return;
    }
    if (!hasExisting && !form.smtp_password) {
      toast.error(t('smtp_password_required_new'));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      const res = await fetch(`${API}/api/smtp-settings`, {
        method: 'POST', headers, body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      const data = await res.json();
      setIsVerified(data.is_verified || false);
      setLastTested(data.last_tested || null);
      setHasExisting(true);
      toast.success(t('smtp_saved'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testForm.recipient) {
      toast.error(t('smtp_enter_recipient'));
      return;
    }
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/smtp-settings/test`, {
        method: 'POST', headers,
        body: JSON.stringify(testForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Test failed');
      setIsVerified(true);
      setLastTested(new Date().toISOString());
      toast.success(data.message || t('smtp_test_success'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center h-64" data-testid="smtp-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="smtp-settings-page">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]" data-testid="smtp-page-title">{t('smtp_title')}</h1>
            <p className="text-slate-500 mt-1">{t('smtp_subtitle')}</p>
          </div>
          <div data-testid="smtp-verification-badge">
            {isVerified ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200">
                <CheckCircle2 className="w-4 h-4" /> {t('smtp_verified')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium border border-amber-200">
                <AlertTriangle className="w-4 h-4" /> {t('smtp_not_verified')}
              </span>
            )}
          </div>
        </div>

        {/* SMTP Configuration Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Server className="w-5 h-5" /> {t('smtp_config_title')}
              </CardTitle>
              <CardDescription>{t('smtp_config_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Preset Selector */}
              <div>
                <Label>{t('smtp_preset')}</Label>
                <Select value={selectedPreset} onValueChange={applyPreset}>
                  <SelectTrigger className="mt-1" data-testid="smtp-preset-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SMTP_PRESETS.map(p => (
                      <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">{t('smtp_preset_hint')}</p>
              </div>

              {/* Host + Port */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('smtp_host')} *</Label>
                  <Input
                    data-testid="smtp-host-input"
                    className="mt-1"
                    placeholder="mail.example.com"
                    value={form.smtp_host}
                    onChange={e => setForm(p => ({ ...p, smtp_host: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>{t('smtp_port')}</Label>
                  <Input
                    data-testid="smtp-port-input"
                    className="mt-1"
                    type="number"
                    value={form.smtp_port}
                    onChange={e => handlePortChange(e.target.value)}
                  />
                </div>
              </div>

              {/* Username + Password */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('smtp_username')} *</Label>
                  <Input
                    data-testid="smtp-username-input"
                    className="mt-1"
                    placeholder="user@example.com"
                    value={form.smtp_username}
                    onChange={e => setForm(p => ({ ...p, smtp_username: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>{t('smtp_password')} {hasExisting && <span className="text-xs text-slate-400 font-normal">({t('smtp_password_hint')})</span>}</Label>
                  <Input
                    data-testid="smtp-password-input"
                    className="mt-1"
                    type="password"
                    placeholder={hasExisting ? '••••••••' : ''}
                    value={form.smtp_password}
                    onChange={e => setForm(p => ({ ...p, smtp_password: e.target.value }))}
                  />
                </div>
              </div>

              {/* From Email + From Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('smtp_from_email')} *</Label>
                  <Input
                    data-testid="smtp-from-email-input"
                    className="mt-1"
                    placeholder="noreply@example.com"
                    value={form.from_email}
                    onChange={e => setForm(p => ({ ...p, from_email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>{t('smtp_from_name')} *</Label>
                  <Input
                    data-testid="smtp-from-name-input"
                    className="mt-1"
                    placeholder="My Company"
                    value={form.from_name}
                    onChange={e => setForm(p => ({ ...p, from_name: e.target.value }))}
                  />
                </div>
              </div>

              {/* TLS Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#1e3a5f]" />
                    <span className="font-medium text-sm text-[#1e3a5f]">{t('smtp_tls')}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {form.smtp_port === 465
                      ? t('smtp_tls_465')
                      : t('smtp_tls_587')}
                  </p>
                </div>
                <Switch
                  data-testid="smtp-tls-toggle"
                  checked={form.use_tls}
                  disabled={form.smtp_port === 465}
                  onCheckedChange={v => setForm(p => ({ ...p, use_tls: v }))}
                />
              </div>

              {/* Save Button */}
              <Button
                data-testid="smtp-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {t('smtp_save')}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Test Email Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Send className="w-5 h-5" /> {t('smtp_test_title')}
              </CardTitle>
              <CardDescription>{t('smtp_test_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t('smtp_test_recipient')}</Label>
                <Input
                  data-testid="smtp-test-recipient"
                  className="mt-1"
                  type="email"
                  placeholder="you@gmail.com"
                  value={testForm.recipient}
                  onChange={e => setTestForm(p => ({ ...p, recipient: e.target.value }))}
                />
              </div>
              <div>
                <Label>{t('smtp_test_message')}</Label>
                <Textarea
                  data-testid="smtp-test-message"
                  className="mt-1"
                  rows={3}
                  placeholder={t('smtp_test_message_placeholder')}
                  value={testForm.message}
                  onChange={e => setTestForm(p => ({ ...p, message: e.target.value }))}
                />
              </div>
              <Button
                data-testid="smtp-test-send-btn"
                onClick={handleTest}
                disabled={testing || !hasExisting}
                className="bg-[#22c55e] hover:bg-[#16a34a] text-white"
              >
                {testing ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('smtp_sending')}</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> {t('smtp_send_test')}</>
                )}
              </Button>
              {!hasExisting && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Info className="w-3 h-3" /> {t('smtp_save_first')}
                </p>
              )}
              {lastTested && (
                <p className="text-xs text-slate-400" data-testid="smtp-last-tested">
                  {t('smtp_last_tested')}: {new Date(lastTested).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </ShopLayout>
  );
}
